"use client";
import { useStore } from "@nanostores/react";
import { useCallback, useEffect } from "react";
import { decodeJwtToken } from "@climatepartner/utils";
import { logout, getPkceState, fetchOpenIdConfig, redirectToAuthUrl, handleLogoutTimer, refreshAccessToken, handleTokenRefreshTimer, fetchAndStoreAccessToken, } from "./actions";
import { hasValidToken, getUserFromAccessToken, getCodeStateQueryParams } from "./helpers";
import { storeAndReturnCurrentRealm } from "./helpers/storeAndReturnCurrentRealm";
import { accessToken, accessTokenExp, openIdConfig, refreshToken, refreshTokenExp } from "./store";
export const useMakeAuth = (authOptions) => {
    const { openidRealmConfig, defaultRealm = "customer" } = authOptions;
    const idConfig = useStore(openIdConfig);
    const accessTokenValue = useStore(accessToken);
    const refreshTokenValue = useStore(refreshToken);
    const accessTokenExpValue = useStore(accessTokenExp);
    const refreshTokenExpValue = useStore(refreshTokenExp);
    const user = accessTokenValue ? getUserFromAccessToken(accessTokenValue) : null;
    const isAuthenticated = user !== null && !!accessTokenValue && +accessTokenExpValue > Date.now();
    const isAccessTokenInvalid = !hasValidToken(accessTokenValue, +accessTokenExpValue);
    const isRefreshTokenInvalid = !hasValidToken(refreshTokenValue, +refreshTokenExpValue);
    const currentRealm = storeAndReturnCurrentRealm({ defaultRealm, openidRealmConfig });
    if (!Object.prototype.hasOwnProperty.call(authOptions.openidRealmConfig, authOptions.defaultRealm)) {
        if (process.env.NODE_ENV !== "production") {
            console.error("Default realm from props is not found in the config", authOptions.openidRealmConfig);
        }
        throw new Error("Invalid realm");
    }
    const shouldFetchAccessToken = useCallback((stateParam, codeParam, data) => codeParam && stateParam === getPkceState() && data.token_endpoint, []);
    const handleInvalidAccessTokenAndRefreshToken = useCallback((data) => {
        const { stateParam, codeParam } = getCodeStateQueryParams();
        if (shouldFetchAccessToken(stateParam, codeParam, data)) {
            fetchAndStoreAccessToken(codeParam, currentRealm.scope);
        }
        else {
            redirectToAuthUrl(data.authorization_endpoint, currentRealm.clientId);
        }
    }, [currentRealm.clientId, currentRealm.scope, shouldFetchAccessToken]);
    const handleOpenIdConfig = useCallback((data) => {
        if (isAccessTokenInvalid && isRefreshTokenInvalid) {
            handleInvalidAccessTokenAndRefreshToken(data);
        }
    }, [handleInvalidAccessTokenAndRefreshToken, isAccessTokenInvalid, isRefreshTokenInvalid]);
    useEffect(() => {
        if (!idConfig) {
            fetchOpenIdConfig(currentRealm.url).then(handleOpenIdConfig);
            return;
        }
        if (+refreshTokenExpValue > Date.now()) {
            handleTokenRefreshTimer(+accessTokenExpValue);
            if (!getUserFromAccessToken(accessTokenValue)?.isCpEmployee) {
                handleLogoutTimer(+refreshTokenExpValue);
            }
        }
    }, [accessTokenExpValue, accessTokenValue, currentRealm.url, handleOpenIdConfig, idConfig, refreshTokenExpValue]);
    return [
        {
            user,
            isAuthenticated,
            accessToken: accessTokenValue,
            accessTokenExp: +accessTokenExpValue,
            decodedAccessToken: accessTokenValue ? decodeJwtToken(accessTokenValue) : null,
            refreshToken: refreshTokenValue,
            refreshTokenExp: +refreshTokenExpValue,
        },
        {
            refreshAccessToken: refreshAccessToken,
            logout: logout,
        },
    ];
};
//# sourceMappingURL=useMakeAuth.js.map